// src/pages/AuthCallback.jsx
import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  getMyPortalAccess,
  resolveDestinationFromAccess,
} from "../../lib/access/portalAccess";
import { claimStoredReferralCode } from "../../lib/referrals/referrals";

function normalizeRedirectTarget(value) {
  if (!value) return "";

  try {
    const url = new URL(String(value).trim(), window.location.origin);
    if (url.origin !== window.location.origin) return "";

    const path = `${url.pathname}${url.search}${url.hash}`;
    // Never redirect back into authentication or protected staff portals.
    if (
      /^\/(?:auth\/callback|login|register|forgot-password|admin(?:\/|$)|partner(?:\/|$)|accept-cohort-invite)(?:[/?#]|$)/i.test(path)
    ) {
      return "";
    }

    return path;
  } catch {
    return "";
  }
}

function goToTarget(target, navigate) {
  navigate(target, { replace: true });
}

async function claimReferralIfPossible() {
  try {
    await claimStoredReferralCode(
      supabase
    );
  } catch (error) {
    console.warn(
      "Referral claim skipped:",
      error
    );
  }
}

export default function AuthCallback() {
  const navigate = useNavigate();

  const {
    search,
    hash,
  } = useLocation();

  const timeouts = useRef([]);
  const ran = useRef(false);
  const completed = useRef(false);

  const params =
    new URLSearchParams(search);

  const errorDescription =
    params.get("error_description");

  const code = params.get("code");
  const tokenHash = params.get("token_hash");
  const tokenType = params.get("type");

  const redirectTo =
    normalizeRedirectTarget(
      params.get("redirectTo")
    );

  // Only invite tokens carried by a successful verification callback are
  // used here. The acceptance RPC later verifies the authenticated email.
  const cohortInviteToken = String(params.get("cohortInviteToken") || "").trim();

  const isRecovery =
    tokenType === "recovery" ||
    (
      typeof hash === "string" &&
      hash.includes("type=recovery")
    );

  const hasAuthHash =
    typeof hash === "string" &&
    /access_token=|refresh_token=|provider_token=/.test(
      hash
    );

  const [phase, setPhase] = useState(
    errorDescription
      ? "error"
      : isRecovery
      ? "recovery"
      : code || tokenHash || hasAuthHash
      ? "verified"
      : "missing"
  );

  const [errMsg, setErrMsg] =
    useState(
      errorDescription || ""
    );

  const [pw1, setPw1] =
    useState("");

  const [pw2, setPw2] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [
    successTarget,
    setSuccessTarget,
  ] = useState("/dashboard");

  const loginBackPath = "/login";

  async function resolveCallbackDestination(
    currentUser
  ) {
    if (!currentUser?.id) {
      throw new Error(
        "The signed-in user could not be loaded."
      );
    }

    if (cohortInviteToken) {
      // Do not let an onboarding redirect skip accepting the pending invite.
      // The acceptance RPC enforces that the authenticated email is invited.
      if (cohortInviteToken.length > 2048 || /[\s\x00-\x1F]/.test(cohortInviteToken)) {
        throw new Error("The cohort invitation token is invalid.");
      }
      return `/accept-cohort-invite?token=${encodeURIComponent(cohortInviteToken)}`;
    }

    const access =
      await getMyPortalAccess();

    const result =
      await resolveDestinationFromAccess({
        access,
        userId: currentUser.id,
        beforeLearnerResolve:
          claimReferralIfPossible,
      });

    /*
     * Admin and partner destinations must
     * always win over a requested learner
     * redirect.
     */
    if (
      access?.account_type !==
      "learner"
    ) {
      return (
        result?.destination ||
        "/login"
      );
    }

    /*
     * An incomplete learner profile must
     * still go through profile gate.
     */
    if (
      result?.destination ===
      "/profile-gate"
    ) {
      return "/profile-gate";
    }

    /*
     * A fully onboarded learner may
     * continue to the requested route.
     */
    return (
      redirectTo ||
      result?.destination ||
      "/dashboard"
    );
  }

  async function finishSignedInUser(
    currentUser,
    delay = 0
  ) {
    if (
      completed.current ||
      !currentUser?.id
    ) {
      return;
    }

    const destination =
      await resolveCallbackDestination(
        currentUser
      );

    if (completed.current) return;

    completed.current = true;

    setSuccessTarget(destination);

    cleanUrl();
    setPhase("success");

    if (delay > 0) {
      const id = window.setTimeout(
        () =>
          goToTarget(
            destination,
            navigate
          ),
        delay
      );

      timeouts.current.push(id);
      return;
    }

    goToTarget(
      destination,
      navigate
    );
  }

  useEffect(() => {
    if (ran.current) return;

    ran.current = true;

    const clearAll = () => {
      timeouts.current.forEach(
        (timeoutId) =>
          clearTimeout(timeoutId)
      );

      timeouts.current = [];
    };

    const pushTimeout = (
      callback,
      milliseconds
    ) => {
      const id = setTimeout(
        callback,
        milliseconds
      );

      timeouts.current.push(id);

      return id;
    };

    if (
      errorDescription ||
      (
        !code &&
        !tokenHash &&
        !hasAuthHash &&
        !isRecovery
      )
    ) {
      return () => clearAll();
    }

    (async () => {
      if (isRecovery) {
        try {
          if (tokenHash) {
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: "recovery",
            });
            if (error) throw error;
          } else if (code) {
            const { error } =
              await supabase.auth.exchangeCodeForSession(code);
            if (error) throw error;
          } else if (hasAuthHash) {
            const { data, error } = await supabase.auth.getSession();
            if (error || !data?.session) {
              throw new Error(error?.message || "Recovery session not found.");
            }
          }

          const { data, error } = await supabase.auth.getSession();
          if (error || !data?.session) {
            throw new Error("Recovery session not found. Please open the latest reset link again.");
          }

          setPhase("recovery");
          return;
        } catch (error) {
          setErrMsg(
            error?.message ||
              "Could not start password recovery."
          );

          setPhase("error");
          return;
        }
      }

      try {
        const {
          data,
        } =
          await supabase.auth.getSession();

        if (data?.session?.user && !code && !tokenHash && !hasAuthHash) {
          await finishSignedInUser(
            data.session.user,
            300
          );

          return;
        }
      } catch (error) {
        console.warn(
          "Existing callback session check failed:",
          error
        );
      }

      setPhase("verified");

      pushTimeout(
        async () => {
          setPhase("finishing");

          try {
            if (code) {
              const {
                error:
                  exchangeError,
              } =
                await supabase.auth.exchangeCodeForSession(code);

              if (exchangeError) {
                throw exchangeError;
              }

              const {
                data,
              } =
                await supabase.auth.getSession();

              await finishSignedInUser(
                data?.session?.user,
                500
              );

              return;
            }

            if (tokenHash) {
              const allowedTypes = ["signup", "email", "magiclink", "invite", "email_change"];
              if (!allowedTypes.includes(tokenType)) {
                throw new Error("Unsupported email verification link type.");
              }
              const { data, error } = await supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: tokenType,
              });
              if (error) throw error;
              const session = data?.session ||
                (await supabase.auth.getSession()).data?.session;
              if (!session?.user) throw new Error("No session was established by the verification link.");
              await finishSignedInUser(session.user, 500);
              return;
            }

            if (hasAuthHash) {
              const {
                data,
                error,
              } =
                await supabase.auth.getSession();

              if (
                error ||
                !data?.session
              ) {
                throw new Error(
                  error?.message ||
                    "No session found in URL."
                );
              }

              await finishSignedInUser(
                data.session.user,
                500
              );

              return;
            }

            setPhase("error");
            setErrMsg(
              "No authentication credentials were found in the URL."
            );
          } catch (error) {
            console.error(
              "Authentication callback failed:",
              error
            );

            setErrMsg(
              error?.message ||
                "Unexpected error completing sign in."
            );

            setPhase("error");
          }
        },
        250
      );

      pushTimeout(
        async () => {
          if (completed.current) return;

          try {
            const {
              data,
            } =
              await supabase.auth.getSession();

            if (
              !code && !tokenHash && !hasAuthHash &&
              data?.session?.user
            ) {
              await finishSignedInUser(
                data.session.user
              );
            }
          } catch (error) {
            console.warn(
              "Authentication callback fallback failed:",
              error
            );
          }
        },
        6000
      );
    })();

    return () => clearAll();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    code,
    tokenHash,
    tokenType,
    hasAuthHash,
    isRecovery,
    errorDescription,
    navigate,
    redirectTo,
    cohortInviteToken,
  ]);

  async function handleSetPassword(
    event
  ) {
    event.preventDefault();

    setErrMsg("");

    if (
      !pw1 ||
      pw1.length < 8
    ) {
      setErrMsg(
        "Password must be at least 8 characters."
      );

      return;
    }

    if (pw1 !== pw2) {
      setErrMsg(
        "Passwords do not match."
      );

      return;
    }

    try {
      setSaving(true);

      const {
        error,
      } =
        await supabase.auth.updateUser({
          password: pw1,
        });

      if (error) throw error;

      const {
        data,
      } =
        await supabase.auth.getSession();

      completed.current = false;

      await finishSignedInUser(
        data?.session?.user
      );
    } catch (error) {
      setErrMsg(
        error?.message ||
          "Failed to update password."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        {phase === "recovery" && (
          <div className="rounded-xl border border-gray-200 p-6 shadow-sm bg-white text-left">
            <h1 className="text-xl font-bold text-black text-center">
              Set a New Password
            </h1>

            {errMsg && (
              <p className="mt-2 text-sm text-red-600 text-center">
                {errMsg}
              </p>
            )}

            <form
              onSubmit={
                handleSetPassword
              }
              className="mt-6 space-y-5"
            >
              <div>
                <label className="block text-black text-sm font-semibold mb-1">
                  New Password
                </label>

                <input
                  type="password"
                  value={pw1}
                  onChange={(event) =>
                    setPw1(
                      event.target.value
                    )
                  }
                  className="w-full bg-transparent border-b border-neutral-500 pb-2 text-black placeholder-black/35 text-sm focus:outline-none"
                  autoComplete="new-password"
                  placeholder="Enter a new password"
                />
              </div>

              <div>
                <label className="block text-black text-sm font-semibold mb-1">
                  Confirm Password
                </label>

                <input
                  type="password"
                  value={pw2}
                  onChange={(event) =>
                    setPw2(
                      event.target.value
                    )
                  }
                  className="w-full bg-transparent border-b border-neutral-500 pb-2 text-black placeholder-black/35 text-sm focus:outline-none"
                  autoComplete="new-password"
                  placeholder="Re-enter the new password"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-neutral-700 text-white text-lg font-bold py-3 rounded-[25px] hover:bg-neutral-800 transition-colors disabled:opacity-50"
              >
                {saving
                  ? "Saving…"
                  : "Update Password"}
              </button>

              <p className="text-center text-xs text-black/70">
                Stuck?{" "}
                <Link
                  to="/forgot-password"
                  className="text-[#E9652D] hover:underline"
                >
                  Request a new reset link
                </Link>
                .
              </p>
            </form>
          </div>
        )}

        {phase === "verified" && (
          <StateCard
            title="You're verified! 🎉"
            subtitle="Finishing your sign-in now…"
            spinner
          />
        )}

        {phase === "finishing" && (
          <StateCard
            title="Signing you in…"
            subtitle="Checking your Gwin account access…"
            spinner
          />
        )}

        {phase === "success" && (
          <StateCard
            title="All set! ✅"
            subtitle="Redirecting you…"
          >
            <a
              href={successTarget}
              className="inline-block mt-4 px-5 py-2 rounded-full bg-black text-white font-medium hover:bg-neutral-800"
            >
              Go now
            </a>
          </StateCard>
        )}

        {phase === "error" && (
          <StateCard
            title="We couldn't complete sign-in"
            subtitle={
              errMsg ||
              "Please try again."
            }
            error
          >
            <div className="mt-4 space-x-2">
              <Link
                to={loginBackPath}
                className="inline-block px-5 py-2 rounded-full bg-black text-white font-medium hover:bg-neutral-800"
              >
                Back to Login
              </Link>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
                className="inline-block px-5 py-2 rounded-full border-2 border-black text-black font-medium hover:bg-gray-50"
              >
                Retry
              </button>
            </div>
          </StateCard>
        )}

        {phase === "missing" && (
          <StateCard
            title="Nothing to do here"
            subtitle="No authentication code was found."
          >
            <Link
              to={loginBackPath}
              className="inline-block mt-4 px-5 py-2 rounded-full bg-black text-white font-medium hover:bg-neutral-800"
            >
              Go to Login
            </Link>
          </StateCard>
        )}
      </div>
    </div>
  );
}

function cleanUrl() {
  try {
    const clean =
      `${window.location.origin}${window.location.pathname}`;

    window.history.replaceState(
      {},
      document.title,
      clean
    );
  } catch {}
}

function StateCard({
  title,
  subtitle,
  spinner = false,
  error = false,
  children,
}) {
  return (
    <div className="rounded-xl border border-gray-200 p-6 shadow-sm bg-white">
      <div className="flex flex-col items-center text-center">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${
            error
              ? "bg-red-100"
              : "bg-green-100"
          }`}
        >
          {spinner ? (
            <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-2xl">
              {error ? "⚠️" : "✅"}
            </span>
          )}
        </div>

        <h1 className="text-xl font-bold text-black">
          {title}
        </h1>

        {subtitle && (
          <p className="mt-2 text-sm text-gray-600">
            {subtitle}
          </p>
        )}

        {children}
      </div>
    </div>
  );
}